import { Clapperboard, RefreshCw, TriangleAlert } from 'lucide-react'
import { useEffect, useState, type RefObject } from 'react'
import { isApiError } from '../../api/client'
import { useStartStoryboard, useStoryboard, type StoryboardFrame } from '../../api/storyboard'
import type { PostDetail } from '../../api/types'
import { seekVideo } from '../../lib/cardControls'
import { formatTimecode, plural } from '../../lib/format'
import { Button } from '../Button'
import { Spinner } from '../Spinner'

/** Раскадровка «кадр + фраза»: строится по кнопке из ролика в кэше, клик по кадру перематывает плеер. */
export function Storyboard({ post, videoRef }: { post: PostDetail; videoRef: RefObject<HTMLVideoElement | null> }) {
  const isReel = post.kind === 'reel'
  const query = useStoryboard(post.id, isReel)
  const start = useStartStoryboard(post.id)

  if (!isReel) return <p className="text-sm text-muted">У карусели и картинки раскадровки нет.</p>
  if (query.isPending) {
    return (
      <div className="flex justify-center py-6">
        <Spinner />
      </div>
    )
  }
  if (query.isError) {
    return (
      <div className="flex flex-col items-start gap-2 text-sm text-muted">
        Не удалось загрузить раскадровку.
        <Button size="sm" onClick={() => void query.refetch()}>
          Повторить
        </Button>
      </div>
    )
  }

  const board = query.data
  const videoCached = post.video_cached || board.video_cached
  const running = board.status === 'running' || start.isPending
  const noVideo = isApiError(start.error, 409)
  const run = () => start.mutate()

  if (running) {
    return (
      <div className="flex items-center gap-3 rounded-xl bg-surface px-4 py-4 text-sm ring-1 ring-line ring-inset">
        <Spinner />
        <div>
          <div className="font-medium">Разбиваю на кадры…</div>
          <div className="text-muted">Ищу смены сцен и подбираю фразы. Обычно до минуты.</div>
        </div>
      </div>
    )
  }

  if (board.status === 'none') {
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="text-sm text-pretty text-muted">
          Ключевые кадры ролика и фраза, которая звучит на каждом. Клик по кадру перематывает плеер.
        </p>
        <Button variant="primary" onClick={run} disabled={!videoCached}>
          <Clapperboard size={16} />
          Разбить на кадры
        </Button>
        {(!videoCached || noVideo) && (
          <p className="text-sm text-pretty text-muted">
            {post.has_video || post.video_cached
              ? 'Ролик ещё не у нас. Запусти его в плеере: когда он загрузится, раскадровку можно будет построить.'
              : 'Ролика нет в базе — раскадровку не построить.'}
          </p>
        )}
        {start.isError && !noVideo && <p className="text-sm text-danger">Не удалось запустить. Попробуй ещё раз.</p>}
      </div>
    )
  }

  const frames = board.frames
  return (
    <div className="flex flex-col gap-3">
      {board.status === 'failed' && (
        <div className="flex items-start gap-2 rounded-lg bg-surface px-3 py-2 text-sm ring-1 ring-line ring-inset">
          <TriangleAlert size={16} className="mt-0.5 shrink-0 text-muted" />
          <span>
            Раскадровка не получилась{board.error ? `: ${board.error}` : ''}.
            {frames.length > 0 && <span className="text-muted"> Ниже — прошлая.</span>}
          </span>
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-muted tabular-nums">
          {frames.length > 0 ? `${frames.length} ${plural(frames.length, 'кадр', 'кадра', 'кадров')}` : 'Кадров нет'}
        </span>
        <Button
          size="sm"
          onClick={run}
          disabled={!videoCached}
          title={videoCached ? undefined : 'Ролика нет в кэше — сначала запусти его в плеере'}
        >
          <RefreshCw size={14} />
          {board.status === 'failed' ? 'Попробовать снова' : 'Перегенерировать'}
        </Button>
      </div>
      {noVideo && (
        <p className="text-sm text-muted">Ролика нет в кэше. Запусти его в плеере и попробуй снова.</p>
      )}
      {frames.length > 0 && <FrameGrid frames={frames} videoRef={videoRef} />}
    </div>
  )
}

function FrameGrid({ frames, videoRef }: { frames: StoryboardFrame[]; videoRef: RefObject<HTMLVideoElement | null> }) {
  const current = useCurrentFrame(frames, videoRef)

  // На телефоне плеер выше раскадровки — перемотка показывает и его.
  const seek = (t: number) => seekVideo(videoRef, t)

  return (
    <ol className="grid grid-cols-2 gap-x-3 gap-y-4 desk:grid-cols-3">
      {frames.map((f, i) => (
        <li key={f.image_url} className="min-w-0">
          <button
            type="button"
            onClick={() => seek(f.t)}
            title="Перемотать сюда"
            className={`relative block aspect-[9/16] w-full overflow-hidden rounded-lg bg-black ring-2 transition-shadow ${
              i === current ? 'ring-fg' : 'ring-transparent hover:ring-line'
            }`}
          >
            <img src={f.image_url} alt="" loading="lazy" className="size-full object-contain" />
            <span className="absolute bottom-1.5 left-1.5 rounded bg-black/70 px-1.5 py-0.5 font-mono text-[11px] leading-none text-white tabular-nums">
              {formatTimecode(f.t)}
            </span>
          </button>
          <p className={`mt-1.5 text-[13px] leading-snug text-pretty desk:text-xs ${f.text ? '' : 'text-muted'}`}>
            {f.text || 'без слов'}
          </p>
        </li>
      ))}
    </ol>
  )
}

function useCurrentFrame(frames: StoryboardFrame[], videoRef: RefObject<HTMLVideoElement | null>): number {
  const [current, setCurrent] = useState(-1)
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    const update = () => {
      if (v.paused && v.currentTime === 0) return setCurrent(-1)
      let idx = -1
      frames.forEach((f, i) => {
        if (f.t <= v.currentTime + 0.05) idx = i
      })
      setCurrent(idx)
    }
    v.addEventListener('timeupdate', update)
    v.addEventListener('seeked', update)
    return () => {
      v.removeEventListener('timeupdate', update)
      v.removeEventListener('seeked', update)
    }
  }, [frames, videoRef])
  return current
}
